/**
 * Display rendering module for RPN Calculator
 * Handles sprite-based LCD digit rendering and formatting
 */

import type { CommaType, DisplayMode } from '../calculator/types';

/**
 * Add number separators to a string
 */
function addSeparators(
  numStr: string,
  inDecimal: string,
  outDecimal: string,
  separator: string
): string {
  let str = numStr + '';
  const decPos = str.indexOf(inDecimal);
  let strEnd = '';

  if (decPos !== -1) {
    strEnd = outDecimal + str.substring(decPos + 1);
    str = str.substring(0, decPos);
  }

  const rgx = /(\d+)(\d{3})/;
  while (rgx.test(str)) {
    str = str.replace(rgx, '$1' + separator + '$2');
  }

  return str + strEnd;
}

/**
 * Format a number for display based on comma type preference
 */
export function formatNumber(data: string, commaType: CommaType): string {
  switch (commaType) {
    case '1': // No separators, comma before fraction
      return addSeparators(data, '.', ',', 'x');
    case '2': // No separators, dot before fraction
      return addSeparators(data, '.', '.', 'x');
    case '3': // Comma as separator, dot before fraction
      return addSeparators(data, '.', '.', ',');
    case '4': // Dot as separator, comma before fraction
      return addSeparators(data, '.', ',', '.');
    default:
      return data;
  }
}

/**
 * Render a number as LCD sprite-based digits
 */
export function renderDisplay(data: string, commaType: CommaType): string {
  // Handle errors and edge cases
  if (
    data === 'undefined' ||
    data === 'NaN' ||
    data === 'Infinity' ||
    data === '-Infinity' ||
    data === 'ERROR'
  ) {
    data = 'Krr0r'; // Error display pattern
  }

  // Handle empty display
  if (data === '') {
    data = '0';
    const displayPrecision = parseInt(localStorage.getItem('displayprecision') || '10');
    const displayMode = localStorage.getItem('displaymode') as DisplayMode | null;

    if (displayMode && displayMode !== 'normal') {
      data = '0.' + '0'.repeat(displayPrecision);
    }
  }

  // Apply number formatting
  data = formatNumber(data, commaType);

  let output = '';
  let commaDone = false;
  let digitCount = 0;

  for (let i = 0; i < data.length; i++) {
    const char = data[i];

    // Handle digits and special characters
    if (
      (parseInt(char) > -1 && !isNaN(parseInt(char))) ||
      ['a', 'b', 'c', 'd', 'e', 'f', 'r'].includes(char.toLowerCase())
    ) {
      output += `<span class="digit n${char.toLowerCase()}"></span>`;
      digitCount++;
    } else if (char === '-') {
      output += '<span class="digit nminus"></span>';
      digitCount++;
    }

    // Handle separators
    if (char === '.') {
      output += '<span class="comma dot"></span>';
      commaDone = true;
    } else if (char === ',') {
      output += '<span class="comma cm"></span>';
      commaDone = true;
    } else if (char === '+') {
      commaDone = true;
    } else if (char === 'e' || char === 'E') {
      output += '<span class="digit"></span><span class="comma"></span>';
      commaDone = true;
    } else if (char === 'K') {
      output += '<span class="digit ne"></span><span class="comma"></span>';
      digitCount++;
      commaDone = true;
    } else if (char === 'x') {
      commaDone = true;
    }

    // Add empty comma placeholder if needed
    if (!commaDone && data[i + 1] !== '.' && data[i + 1] !== ',') {
      output += '<span class="comma"></span>';
    }

    commaDone = false;
  }

  // Pad with empty digits to fill display
  const padding = '<span class="digit"></span><span class="comma"></span>';
  for (let j = 0; j < 21 - digitCount; j++) {
    output = padding + output;
  }

  return output;
}

/**
 * Format a value for stack info display
 */
export function formatStackValue(
  value: number,
  displayMode: DisplayMode,
  displayPrecision: number,
  commaType: CommaType
): string {
  let displayVal = value.toString();

  // Remove leading/trailing dots
  displayVal = displayVal.replace(/^\./, '').replace(/\.$/, '');

  // Apply display mode formatting
  switch (displayMode) {
    case 'fix':
      displayVal = parseFloat(displayVal).toFixed(displayPrecision);
      break;
    case 'sci':
      displayVal = formatSCI(displayVal, displayPrecision);
      break;
    case 'eng':
      displayVal = formatENG(displayVal, displayPrecision);
      break;
  }

  // Format exponent display
  displayVal = displayVal.replace('e', 'E ').replace('+', ' ');

  // Apply number separators (removing placeholder x)
  displayVal = formatNumber(displayVal, commaType).replace(/x/g, '');

  return displayVal;
}

/**
 * Render stack info bar
 */
export function renderStackInfo(
  stack: number[],
  displayMode: DisplayMode,
  displayPrecision: number,
  commaType: CommaType
): string {
  const labels = ['b', 'a', 'z', 'y'];

  return labels
    .map((label, i) => {
      const value = stack[4 - i] ?? 0;
      const formatted = formatStackValue(value, displayMode, displayPrecision, commaType);
      return `<span class="indicator truncating-text">${label}:${formatted}</span>`;
    })
    .join('');
}

/**
 * Format number in scientific notation
 */
function formatSCI(number: string, fix: number): string {
  const num = parseFloat(number);
  if (!Number.isFinite(num)) return number;

  const result = toScientific(number, false);
  if (!result.includes('e')) return result + 'e0';

  const [mantissa, exp] = result.split('e');
  return parseFloat(mantissa).toFixed(fix) + 'e' + (exp || '0');
}

/**
 * Format number in engineering notation (exponents are multiples of 3)
 */
function formatENG(number: string, fix: number): string {
  const adjustedFix = fix + 1;
  let result = toScientific(number, true);

  if (!result.includes('e')) result += 'e0';

  const [mantissa, exp] = result.split('e');

  // Handle mantissa precision
  if (mantissa.includes('.')) {
    const [whole] = mantissa.split('.');
    const decimalPlaces = Math.max(0, adjustedFix - whole.length);
    const formatted = parseFloat(mantissa).toFixed(decimalPlaces);
    return formatted + 'e' + (exp || '0');
  } else {
    if (adjustedFix <= mantissa.length) return result;
    const zeros = '0'.repeat(adjustedFix - mantissa.length);
    return mantissa + '.' + zeros + 'e' + exp;
  }
}

/**
 * Convert number to scientific/engineering notation string
 */
function toScientific(s: string, eng: boolean): string {
  let exponentVal = 0;
  let isNegative = false;

  if (s.length > 0 && s.charAt(0) === '-') {
    isNegative = true;
    s = s.substring(1);
  }

  // Handle existing exponent
  const expParts = s.split(/[eE]/);
  if (expParts.length > 1) {
    exponentVal = parseInt(expParts[1]);
    s = expParts[0];
  }

  // Handle decimal point
  const decParts = s.split('.');
  if (decParts.length > 1) {
    s = decParts[0] + decParts[1];
    exponentVal += decParts[0].length - 1;
  } else {
    exponentVal += s.length - 1;
  }

  // Remove leading zeros
  let leadingZeros = 0;
  for (leadingZeros = 0; leadingZeros < s.length && s.charAt(leadingZeros) === '0'; leadingZeros++) {
    exponentVal--;
  }
  s = s.substring(leadingZeros);

  // Determine decimal position
  let moveDecimal: number;
  if (eng) {
    if (exponentVal >= 0) {
      moveDecimal = (exponentVal % 3) + 1;
    } else {
      moveDecimal = 4 - ((-exponentVal) % 3);
      if (moveDecimal === 4) moveDecimal = 1;
    }
    exponentVal -= moveDecimal - 1;
  } else {
    moveDecimal = 1;
  }

  // Build trailing zeros if needed
  let trailingZeros = '';
  for (let i = s.length; i < moveDecimal; i++) {
    trailingZeros += '0';
  }

  // Construct result
  const sign = isNegative ? '-' : '';
  const whole = s.length === 0 ? '0' : s.substring(0, moveDecimal);
  const frac = s.length <= moveDecimal ? trailingZeros : '.' + s.substring(moveDecimal);
  const expPart = s.length === 0 || exponentVal === 0 ? '' : 'e' + exponentVal;

  return sign + whole + frac + expPart;
}
